import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import DialogFooter from '../DialogFooter';
import DialogAlert from '../DialogAlert';
import './MultiPackagePrintScaffold.css';
import {
  isAwaitingMultiPackageOutput,
  multiPackageBatchStatusLabel,
  multiPackageItemStatusLabel,
} from '../../features/multiPackagePrint/multiPackageStatus';
import type {
  ChildPackageCandidate,
  MultiPackagePrintResultItem,
  MultiPackageBatchStatus,
  PrintPackageMode,
  SelectedChildPackage,
} from '../../types/multiPackagePrint';
import {
  childCaseSize,
  isValidLabelCount,
  estimateMultiPackageZipBytes,
  matchesChildPackageSearch,
  retryableResultItems,
  resumableResultItems,
  recommendedLabelCount,
  MULTI_PACKAGE_LIMITS,
  selectionLimitErrors,
  effectiveMultiPackageLimits,
  selectChildPackages,
} from '../../features/multiPackagePrint/childPackageSelection';

type Props = {
  mode?: PrintPackageMode;
  candidates: ChildPackageCandidate[];
  selectedChildren: SelectedChildPackage[];
  sourceCasePlan?: { fullCases: number; remainingUnits: number; hasPartialCase: boolean } | null;
  results?: MultiPackagePrintResultItem[];
  batchStatus?: MultiPackageBatchStatus | null;
  onModeChange: (mode: PrintPackageMode) => void;
  onSelectionChange: (children: SelectedChildPackage[]) => void;
  onImport?: (candidate: ChildPackageCandidate, productId?: number) => void;
  importProductIds?: Record<number, number | undefined>;
  onImportProductChange?: (metrcPackageId: number, productId?: number) => void;
  importingMetrcPackageIds?: number[];
  importErrors?: Record<number, string | undefined>;
  onRetry?: (items: MultiPackagePrintResultItem[]) => void;
  onResume?: (items: MultiPackagePrintResultItem[]) => void;
  onGenerate?: () => void;
  onDownloadOutput?: (item: MultiPackagePrintResultItem) => void;
  onDownloadCsv?: (item: MultiPackagePrintResultItem) => void;
  onDownloadZip?: () => void;
  isLoading?: boolean;
  isDownloadingZip?: boolean;
  errorMessage?: string | null;
  productOptions?: Array<{ id: number; name: string }>;
  estimatedZipBytes?: number | null;
  childModeUnavailableReason?: string | null;
  limits?: { children: number; labels: number; estimatedZipBytes: number };
};

export default function MultiPackagePrintScaffold({
  mode: controlledMode,
  candidates,
  selectedChildren,
  sourceCasePlan = null,
  results = [],
  batchStatus = null,
  onModeChange,
  onSelectionChange,
  onImport,
  importProductIds = {},
  onImportProductChange,
  importingMetrcPackageIds = [],
  importErrors = {},
  onRetry,
  onResume,
  onGenerate,
  onDownloadOutput,
  onDownloadCsv,
  onDownloadZip,
  isLoading = false,
  isDownloadingZip = false,
  errorMessage = null,
  productOptions = [],
  estimatedZipBytes = null,
  childModeUnavailableReason = null,
  limits: configuredLimits = MULTI_PACKAGE_LIMITS,
}: Props) {
  const limits = effectiveMultiPackageLimits(configuredLimits);
  const [uncontrolledMode, setUncontrolledMode] = useState<PrintPackageMode>('source');
  const mode = controlledMode ?? uncontrolledMode;
  const [showPicker, setShowPicker] = useState(false);
  const [query, setQuery] = useState('');
  const [draftSelection, setDraftSelection] = useState<number[]>([]);
  const pickerButtonRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const selectedIds = useMemo(
    () => new Set(selectedChildren.map((child) => child.metrcPackageId)),
    [selectedChildren],
  );
  const filteredCandidates = useMemo(
    () => candidates.filter((candidate) => matchesChildPackageSearch(candidate.packageLabel, query)),
    [candidates, query],
  );

  const chooseMode = (nextMode: PrintPackageMode) => {
    if (controlledMode === undefined) setUncontrolledMode(nextMode);
    onModeChange(nextMode);
  };

  const openPicker = () => {
    setDraftSelection([...selectedIds]);
    setShowPicker(true);
  };

  const confirmSelection = () => {
    onSelectionChange(selectChildPackages(candidates, draftSelection, selectedChildren));
    closePicker();
  };

  useEffect(() => {
    if (!showPicker) return;
    const dialog = dialogRef.current;
    const background: HTMLElement[] = [];
    let branch: HTMLElement | null = dialog;
    while (branch?.parentElement) {
      for (const sibling of Array.from(branch.parentElement.children)) {
        if (sibling !== branch && sibling instanceof HTMLElement && !sibling.inert) {
          sibling.inert = true;
          background.push(sibling);
        }
      }
      branch = branch.parentElement;
      if (branch === document.body) break;
    }
    const focusable = () => Array.from(dialog?.querySelectorAll<HTMLElement>(
      'button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex="0"]',
    ) ?? []);
    focusable()[0]?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowPicker(false);
        window.setTimeout(() => pickerButtonRef.current?.focus(), 0);
        return;
      }
      if (event.key !== 'Tab') return;
      const elements = focusable();
      if (elements.length === 0) return;
      const first = elements[0];
      const last = elements[elements.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      background.forEach((element) => { element.inert = false; });
    };
  }, [showPicker]);

  function closePicker() {
    setShowPicker(false);
    window.setTimeout(() => pickerButtonRef.current?.focus(), 0);
  }

  return (
    <section className="multi-package-print" aria-label="Label package mode" data-testid="multi-package-print-scaffold">
      <div aria-hidden={showPicker || undefined}>
        <fieldset className="multi-package-workflow" disabled={showPicker}>
          <legend className="label">Choose labeling workflow</legend>
          <div className="multi-package-mode-options">
            <label className={`groupedOption multi-package-mode-choice${mode === 'source' ? ' selected' : ''}`}>
              <input
                type="radio"
                name="print-package-mode"
                checked={mode === 'source'}
                onChange={() => chooseMode('source')}
              />
              Labels for this source package
            </label>
            <label className={`groupedOption multi-package-mode-choice${mode === 'children' ? ' selected' : ''}${childModeUnavailableReason ? ' unavailable' : ''}`}>
              <input
                type="radio"
                name="print-package-mode"
                checked={mode === 'children'}
                disabled={Boolean(childModeUnavailableReason)}
                onChange={() => chooseMode('children')}
                aria-describedby={childModeUnavailableReason ? 'child-mode-unavailable-reason' : undefined}
              />
              Labels for existing child packages
            </label>
          </div>
          {childModeUnavailableReason && (
            <p className="muted" id="child-mode-unavailable-reason" role="note">{childModeUnavailableReason}</p>
          )}
          {mode === 'children' && (
            <div className="multi-package-content">
              <div className="multi-package-summary">
                <div>
                  <p className="multi-package-selection-summary" aria-live="polite">{selectedChildren.length} packages selected; {candidates.filter((candidate) => candidate.isEligible && candidate.isImported).length} eligible and imported children available</p>
                  <p className="muted">{candidates.filter((candidate) => !candidate.isImported).length} children are not in Retail ID</p>
                </div>
                <button className="button secondary" ref={pickerButtonRef} type="button" onClick={openPicker}>View child package tags</button>
              </div>
              <p className="muted multi-package-limits">Limits before Generate: {limits.children} packages, {limits.labels.toLocaleString()} labels, {limits.estimatedZipBytes.toLocaleString()} bytes estimated ZIP. Estimated output: {(estimatedZipBytes ?? estimateMultiPackageZipBytes(selectedChildren)).toLocaleString()} bytes.</p>
              {selectionLimitErrors(selectedChildren, estimatedZipBytes ?? estimateMultiPackageZipBytes(selectedChildren), limits).map((error) => (
                <DialogAlert key={error} show message={`${error} Split this selection into smaller runs.`} />
              ))}
              <DialogAlert
                show={selectedChildren.some((child) => !child.productId && !child.productOverrideId)}
                message="Select a product override for each imported child without a mapped product before Generate."
              />
              {selectedChildren.length > 0 && sourceCasePlan && (
                <p className="muted">Source case plan: {sourceCasePlan.fullCases} full {sourceCasePlan.fullCases === 1 ? 'case' : 'cases'}
                  {sourceCasePlan.hasPartialCase ? ` and ${sourceCasePlan.remainingUnits} units in a partial case` : ''}.
                </p>
              )}
              {selectedChildren.length > 0 && (
                <div className="multi-package-table-scroll" role="region" aria-label="Selected child packages table" tabIndex={0}>
                  <table className="table multi-package-table">
                    <caption>Selected child packages</caption>
                    <thead><tr><th>Package tag</th><th>Quantity</th><th>Case plan</th><th>Product override</th><th>Labels</th></tr></thead>
                    <tbody>
                      {selectedChildren.map((child) => (
                        <tr key={child.metrcPackageId}>
                          <td className="multi-package-tag">{child.packageLabel}</td>
                          <td>{child.quantity} {child.unitOfMeasureName}</td>
                          <td>{childCaseSize(child) === null
                            ? 'Case size unavailable'
                            : `${childCaseSize(child)} units in this child package`}</td>
                          <td>
                            <select
                              className="select"
                              aria-label={`Product override for ${child.packageLabel}`}
                              value={child.productOverrideId ?? ''}
                              onChange={(event) => onSelectionChange(selectedChildren.map((selected) => (
                                selected.metrcPackageId === child.metrcPackageId
                                  ? { ...selected, productOverrideId: event.target.value ? Number(event.target.value) : null }
                                  : selected
                              )))}
                            >
                              <option value="">Use mapped product</option>
                              {productOptions.map((product) => <option key={product.id} value={product.id}>{product.name}</option>)}
                            </select>
                          </td>
                          <td>
                            <input
                              className="input multi-package-count"
                              type="number"
                              min="1"
                              step="1"
                              aria-label={`Labels for ${child.packageLabel}`}
                              aria-invalid={child.count !== null && !isValidLabelCount(child.count)}
                              value={child.count ?? ''}
                              onChange={(event) => onSelectionChange(selectedChildren.map((selected) => (
                                selected.metrcPackageId === child.metrcPackageId
                                  ? { ...selected, count: event.target.value ? Number(event.target.value) : null }
                                  : selected
                              )))}
                            />
                            {child.count !== recommendedLabelCount(child.quantity) && (
                              <span className="muted multi-package-count-note" role="note">The recommended count is {recommendedLabelCount(child.quantity)} labels (package units plus 10% rounded up). You can print a different count.</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <DialogAlert show={Boolean(errorMessage)} message={errorMessage ?? undefined} />
              {onGenerate && (
                <div className="multi-package-actions">
                  <button
                    className="button cta"
                    type="button"
                    onClick={onGenerate}
                    disabled={isLoading || isDownloadingZip || selectedChildren.length === 0
                      || selectedChildren.some((child) => !isValidLabelCount(child.count))
                      || selectedChildren.some((child) => !child.productId && !child.productOverrideId)
                      || Boolean(childModeUnavailableReason)
                      || selectionLimitErrors(selectedChildren, estimatedZipBytes ?? estimateMultiPackageZipBytes(selectedChildren), limits).length > 0}
                  >
                    {isLoading ? 'Generating...' : 'Generate'}
                  </button>
                </div>
              )}
            </div>
          )}

          {mode === 'children' && results.length > 0 && (
            <section className="multi-package-results" aria-label="Package generation results">
              {batchStatus && <p className="multi-package-selection-summary" aria-live="polite">Batch status: {multiPackageBatchStatusLabel(batchStatus, results, isLoading || isDownloadingZip)}</p>}
              {results.map((result) => (
                <article className="multi-package-result" key={`${result.metrcPackageId}-${result.sequence}`}>
                  <div>
                    <strong className="multi-package-tag">{result.packageLabel || `METRC package ${result.metrcPackageId}`}</strong>: <span className="badge">{multiPackageItemStatusLabel(result, isLoading || isDownloadingZip)}</span>
                    {result.status === 'RecoveryRequired' && !isAwaitingMultiPackageOutput(result) ? ' — Resume with the same QR range' : ''}
                    {result.errorMessage ? ` - ${result.errorMessage}` : ''}
                  </div>
                  <div className="multi-package-actions">
                    {(result.status === 'Completed' || result.stage === 'OutputPending') && result.printRunId && onDownloadOutput && (
                      <button className="button secondary" type="button" onClick={() => onDownloadOutput(result)}>Download {result.packageLabel || `METRC package ${result.metrcPackageId}`} output</button>
                    )}
                    {(result.status === 'Completed' || result.stage === 'OutputPending') && result.printRunId && onDownloadCsv && (
                      <button className="button secondary" type="button" onClick={() => onDownloadCsv(result)}>Download {result.packageLabel || `METRC package ${result.metrcPackageId}`} CSV</button>
                    )}
                  </div>
                </article>
              ))}
              <div className="multi-package-actions">
                {retryableResultItems(results).length > 0 && onRetry && (
                  <button className="button secondary" type="button" onClick={() => onRetry(retryableResultItems(results))} disabled={isLoading || isDownloadingZip}>Retry incomplete packages</button>
                )}
                {resumableResultItems(results).length > 0 && onResume && (
                  <button className="button secondary" type="button" onClick={() => onResume(resumableResultItems(results))} disabled={isLoading || isDownloadingZip}>Resume recovery</button>
                )}
                {onDownloadZip && results.some((result) => result.status === 'Completed' || result.stage === 'OutputPending') && (
                  <button className="button secondary" type="button" onClick={onDownloadZip} disabled={isLoading || isDownloadingZip}>
                    {isDownloadingZip ? 'Preparing ZIP...' : 'Download completed package ZIP'}
                  </button>
                )}
              </div>
            </section>
          )}
        </fieldset>
      </div>

      {showPicker && typeof document !== 'undefined' && createPortal(
        <div className="multi-package-picker-backdrop">
          <div ref={dialogRef} className="drawer-controls-card multi-package-picker" role="dialog" aria-modal="true" aria-label="Select child packages">
            <div className="multi-package-picker-header">
              <h2 className="modal-title">Select child packages</h2>
              <p className="muted">Review availability, import readiness and eligibility before adding packages.</p>
            </div>
            <div className="multi-package-picker-search">
              <label className="label" htmlFor="child-package-search">Search package tag</label>
              <input className="input filter-search" id="child-package-search" value={query} onChange={(event) => setQuery(event.target.value)} />
            </div>
            <ul className="multi-package-picker-list">
              {filteredCandidates.map((candidate) => {
                const checked = draftSelection.includes(candidate.metrcPackageId);
                const reasonId = `child-package-${candidate.metrcPackageId}-reason`;
                return (
                  <li className="multi-package-candidate" key={candidate.metrcPackageId}>
                    <div className="multi-package-candidate-heading">
                      <label className="multi-package-candidate-choice">
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={!candidate.isEligible || !candidate.isImported}
                          aria-describedby={!candidate.isEligible ? reasonId : undefined}
                          onChange={() => setDraftSelection((current) => (
                            checked
                              ? current.filter((id) => id !== candidate.metrcPackageId)
                              : [...current, candidate.metrcPackageId]
                          ))}
                        />
                        <span>
                          <strong className="multi-package-tag">{candidate.packageLabel}</strong>
                          <span className="muted multi-package-candidate-quantity">({candidate.quantity} {candidate.unitOfMeasureName})</span>
                        </span>
                      </label>
                      <span className="badge">{candidate.isImported ? 'Imported' : 'Not imported'}</span>
                    </div>
                    {!candidate.isEligible && (
                      <span className="multi-package-eligibility" id={reasonId} role="note" tabIndex={0}>{candidate.eligibilityMessage ?? 'Package is not eligible'}</span>
                    )}
                    {candidate.isEligible && !candidate.isImported && (
                      candidate.canImport && onImport
                        ? <div className="multi-package-import">
                          {!candidate.productId && (
                            <select
                              className="select"
                              aria-label={`Product for ${candidate.packageLabel}`}
                              value={importProductIds[candidate.metrcPackageId] ?? ''}
                              onChange={(event) => onImportProductChange?.(
                                candidate.metrcPackageId,
                                event.target.value ? Number(event.target.value) : undefined,
                              )}
                            >
                              <option value="">Select product before import</option>
                              {productOptions.map((product) => <option key={product.id} value={product.id}>{product.name}</option>)}
                            </select>
                          )}
                          <button
                            className="button secondary"
                            type="button"
                            disabled={importingMetrcPackageIds.includes(candidate.metrcPackageId)
                              || (!candidate.productId && !importProductIds[candidate.metrcPackageId])}
                            onClick={() => onImport(candidate, candidate.productId ?? importProductIds[candidate.metrcPackageId])}
                          >
                            {importingMetrcPackageIds.includes(candidate.metrcPackageId)
                              ? `Importing ${candidate.packageLabel}...`
                              : `Import ${candidate.packageLabel}`}
                          </button>
                          <DialogAlert show={Boolean(importErrors[candidate.metrcPackageId])} message={importErrors[candidate.metrcPackageId]} />
                        </div>
                        : <span className="multi-package-eligibility" role="note">{candidate.importBlockMessage ?? 'Import permission is required'}</span>
                    )}
                  </li>
                );
              })}
              {filteredCandidates.length === 0 && <li className="muted multi-package-picker-empty">No child packages match your search.</li>}
            </ul>
            <div className="multi-package-picker-footer">
              <p className="muted" aria-live="polite">{draftSelection.length} packages selected</p>
              <DialogFooter>
                <button className="button secondary" type="button" onClick={closePicker}>Cancel</button>
                <button className="button cta" type="button" onClick={confirmSelection}>Add packages</button>
              </DialogFooter>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </section>
  );
}
